import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip
} from "recharts";
import {
  Activity, Users, Layers, Rocket, ReceiptText,
  TrendingUp, RefreshCw, AlertTriangle
} from "lucide-react";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";

const TABS = [
  "Overview",
  "P&L Intelligence",
  "Execution Report",
  "Risk Analytics",
  "Strategy Intelligence",
  "User Journey"
];

const money = (value) =>
  value == null ? "—" :
  Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2
  });

const number = (value) => Number(value || 0).toLocaleString("en-IN");

function Metric({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="flex justify-between text-xs uppercase tracking-wider text-slate-500">
        {label}
        {Icon && <Icon className="w-4 h-4" />}
      </div>
      <div className="mt-3 text-2xl font-mono font-semibold break-words">{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 p-5 min-w-0">
      <h3 className="font-semibold mb-4">{title}</h3>
      {children}
    </section>
  );
}

function Empty({ children = "No records available." }) {
  return <div className="text-sm text-slate-500 py-8 text-center">{children}</div>;
}

export default function AdminAnalytics() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("tab") || "Overview";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/admin/analytics");
      setData(response.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Unable to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tab = TABS.includes(selected) ? selected : "Overview";
  const overview = data?.overview || {};
  const pnl = data?.pnl || {};
  const risk = data?.risk || {};

  return (
    <Layout>
      <PageHeader
        title="Admin Analytics"
        subtitle="Platform-wide reporting · Read-only DEV data"
      />

      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {TABS.map((name) => (
              <button
                key={name}
                onClick={() => setParams({ tab: name })}
                className={`px-3 py-2 text-xs border ${
                  tab === name
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-300"
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex gap-2 items-center px-3 py-2 border bg-white text-sm disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {error && (
          <div className="border border-rose-200 bg-rose-50 text-rose-700 p-4 text-sm">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="py-16 text-center text-slate-500">Loading analytics…</div>
        ) : data && (
          <>
            {tab === "Overview" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <Metric label="Users" value={number(overview.users)} icon={Users} />
                  <Metric label="Strategies" value={number(overview.strategies)} icon={Layers} />
                  <Metric label="Deployments" value={number(overview.deployments)} icon={Rocket} />
                  <Metric label="Running" value={number(overview.running_deployments)} icon={Activity} />
                  <Metric label="Orders" value={number(overview.orders)} icon={ReceiptText} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Panel title="Order breakdown">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span>Entry orders</span><b>{number(overview.entry_orders)}</b></div>
                      <div className="flex justify-between"><span>Exit orders</span><b>{number(overview.exit_orders)}</b></div>
                      <div className="flex justify-between"><span>Broker connections</span><b>{number(overview.broker_connections)}</b></div>
                    </div>
                  </Panel>
                  <Panel title="User status">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between"><span>Approved / active</span><b>{number(overview.approved_users)}</b></div>
                      <div className="flex justify-between"><span>Users with login recorded</span><b>{number(overview.users_with_login)}</b></div>
                    </div>
                  </Panel>
                </div>
              </>
            )}

            {tab === "P&L Intelligence" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Metric label="Realized P&L" value={money(pnl.realized_pnl)} icon={TrendingUp} />
                  <Metric label="Win rate" value={`${number(pnl.win_rate)}%`} />
                  <Metric label="Winning exits" value={number(pnl.winning_exits)} />
                  <Metric label="Losing exits" value={number(pnl.losing_exits)} />
                </div>
                <Panel title="Daily realized P&L and cumulative realized P&L">
                  {pnl.daily?.length ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={pnl.daily}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip formatter={(v) => money(v)} />
                          <Area dataKey="cumulative_pnl" name="Cumulative P&L" type="monotone" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <Empty>No recorded EXIT P&L history.</Empty>}
                </Panel>
                <div className="grid md:grid-cols-2 gap-4">
                  <Panel title="Gross profit"><div className="text-2xl font-mono">{money(pnl.gross_profit)}</div></Panel>
                  <Panel title="Gross loss"><div className="text-2xl font-mono">{money(pnl.gross_loss)}</div></Panel>
                  <Panel title="Profit factor"><div className="text-2xl font-mono">{pnl.profit_factor ?? "—"}</div></Panel>
                  <Panel title="Data source"><div className="text-sm text-slate-600">{pnl.source}</div></Panel>
                </div>
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-3">
                  <AlertTriangle className="inline w-4 h-4 mr-1" />
                  {pnl.warning}
                </div>
              </>
            )}

            {tab === "Execution Report" && (
              <Panel title="Latest recorded orders">
                {data.execution?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[900px]">
                      <thead className="bg-slate-50 text-left">
                        <tr>{["Date","Symbol","Side","Leg","Qty","Broker","Mode","Status","Avg. price","P&L"].map(x => <th key={x} className="p-3">{x}</th>)}</tr>
                      </thead>
                      <tbody>
                        {data.execution.map((o, i) => (
                          <tr key={`${o.order_id}-${i}`} className="border-t">
                            <td className="p-3">{String(o.date || "—").slice(0, 19)}</td>
                            <td className="p-3">{o.symbol}</td>
                            <td className="p-3">{o.side}</td>
                            <td className="p-3">{o.leg}</td>
                            <td className="p-3">{number(o.quantity)}</td>
                            <td className="p-3">{o.broker}</td>
                            <td className="p-3">{o.mode}</td>
                            <td className="p-3">{o.status}</td>
                            <td className="p-3">{o.price == null ? "—" : number(o.price)}</td>
                            <td className="p-3">{o.pnl == null ? "—" : money(o.pnl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <Empty />}
              </Panel>
            )}

            {tab === "Risk Analytics" && (
              <>
                <div className="grid md:grid-cols-3 gap-3">
                  <Metric label="Recorded max drawdown" value={money(risk.max_recorded_drawdown)} />
                  <Metric label="Open ENTRY records" value={number(risk.open_entry_records)} />
                  <Metric label="Open exposure" value="Not available" />
                </div>
                <Panel title="Risk data limitations">
                  <p className="text-sm text-slate-600">{risk.note}</p>
                  <p className="text-sm text-slate-600 mt-3">Consecutive losses: not reported pending a validated trade-sequencing definition.</p>
                </Panel>
              </>
            )}

            {tab === "Strategy Intelligence" && (
              <Panel title="Strategy-wise realized performance">
                {data.strategies?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left bg-slate-50">
                        <tr>{["Strategy","Exit trades","Wins","Win rate","Realized P&L"].map(x => <th key={x} className="p-3">{x}</th>)}</tr>
                      </thead>
                      <tbody>
                        {data.strategies.map((s) => (
                          <tr key={s.strategy} className="border-t">
                            <td className="p-3">{s.strategy}</td>
                            <td className="p-3">{number(s.trades)}</td>
                            <td className="p-3">{number(s.wins)}</td>
                            <td className="p-3">{number(s.win_rate)}%</td>
                            <td className="p-3 font-mono">{money(s.realized_pnl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <Empty>No strategy-linked EXIT records.</Empty>}
              </Panel>
            )}

            {tab === "User Journey" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <Metric label="Registered users" value={number(data.users.total)} icon={Users} />
                  <Metric label="Approved users" value={number(data.users.approved)} />
                  <Metric label="Users with login" value={number(data.users.active_with_login)} />
                </div>
                <Panel title="Registrations by recorded date">
                  {data.users.registrations?.length ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.users.registrations}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="registrations" name="Registrations" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <Empty />}
                </Panel>
              </>
            )}

            <div className="text-[11px] text-slate-500">
              Read-only analytics · DEV database · Latest 250 execution records displayed.
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
